import { useMemo, useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Gem,
  LockKeyhole,
  Send,
  Sparkles,
  Sunrise,
  Trash2,
  NotebookText,
} from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import PageContainer from "@/components/PageContainer";
import SoftButton from "@/components/SoftButton";
import { addJournalEntry, getProtectItems } from "@/lib/storage";
import { askReflection, reflectionCompanionAvailable } from "@/lib/aiReflect";
import { COMPANION_MODES, getCompanionMode } from "@/lib/companionModes";
import { toast } from "sonner";

const ICONS = {
  gem: Gem,
  sparkles: Sparkles,
  sunrise: Sunrise,
};

const makeModeMap = (factory) =>
  Object.fromEntries(COMPANION_MODES.map((item) => [item.id, factory(item)]));

export default function Talk() {
  const [modeId, setModeId] = useState("clarity");
  const [threads, setThreads] = useState(() => makeModeMap(() => []));
  const [drafts, setDrafts] = useState(() => makeModeMap(() => ""));
  const [sendingByMode, setSendingByMode] = useState(() => makeModeMap(() => false));
  const [includeProtect, setIncludeProtect] = useState(false);
  const generation = useRef(makeModeMap(() => 0));
  useEffect(
    () => () => {
      Object.keys(generation.current).forEach((id) => {
        generation.current[id] += 1;
      });
    },
    []
  );
  const mode = useMemo(() => getCompanionMode(modeId), [modeId]);
  const messages = threads[modeId] || [];
  const text = drafts[modeId] || "";
  const sending = Boolean(sendingByMode[modeId]);

  if (!reflectionCompanionAvailable) {
    return (
      <PageContainer className="pt-7" testid="talk-page">
        <BrandHeader compact />
        <section className="mt-8 rounded-[1.75rem] p-6" style={{ background: "rgba(91,57,84,0.045)" }}>
          <p className="text-[10px] uppercase tracking-[0.30em]" style={{ color: "var(--un-text-3)" }}>Private conversation</p>
          <h1 className="mt-3 text-[34px] leading-tight" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>Talk is not available in this release.</h1>
          <p className="mt-4 text-[14px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>Your private Journal, Pause, Check-In, Safe Reach and other device-first tools still work without sending anything for AI processing.</p>
          <a href="/journal" className="mt-5 inline-flex rounded-full px-5 py-3 text-[13px] font-medium un-press" style={{ background: "var(--un-plum)", color: "#fffaf5" }}>Write privately</a>
        </section>
      </PageContainer>
    );
  }

  const send = async () => {
    const activeId = modeId;
    const activeMode = getCompanionMode(activeId);
    const activeMessages = threads[activeId] || [];
    const clean = (drafts[activeId] || "").trim();
    if (!clean || sendingByMode[activeId]) return;

    const requestGeneration = generation.current[activeId];
    const userMessage = { id: `${Date.now()}-u`, role: "user", text: clean };
    setThreads((current) => ({
      ...current,
      [activeId]: [...(current[activeId] || []), userMessage],
    }));
    setDrafts((current) => ({ ...current, [activeId]: "" }));
    setSendingByMode((current) => ({ ...current, [activeId]: true }));

    try {
      const protectList = includeProtect ? getProtectItems().map((item) => item.label) : [];
      const { response } = await askReflection({
        text: clean,
        mode: "talk",
        protectList,
        companion: activeId,
        history: activeMessages.slice(-8).map(({ role, text }) => ({ role, text })),
      });
      if (generation.current[activeId] !== requestGeneration) return;
      setThreads((current) => ({
        ...current,
        [activeId]: [
          ...(current[activeId] || []),
          { id: `${Date.now()}-a`, role: "assistant", name: activeMode.name, text: response },
        ],
      }));
    } catch (error) {
      if (generation.current[activeId] !== requestGeneration) return;
      const msg =
        error?.userMessage ||
        error?.response?.data?.detail ||
        `${activeMode.value} is unavailable for a moment.`;
      toast(msg, {
        description: "Your words are still in this conversation. Try again when you are ready.",
      });
    } finally {
      if (generation.current[activeId] === requestGeneration) {
        setSendingByMode((current) => ({ ...current, [activeId]: false }));
      }
    }
  };

  const switchMode = (nextId) => {
    if (nextId === modeId) return;
    setModeId(nextId);
  };

  const clearSession = () => {
    generation.current[modeId] += 1;
    setSendingByMode((current) => ({ ...current, [modeId]: false }));
    setThreads((current) => ({ ...current, [modeId]: [] }));
    setDrafts((current) => ({ ...current, [modeId]: "" }));
    toast(`${mode.value} conversation cleared.`, {
      description: "Nothing from this conversation was added to your Journal.",
    });
  };

  const saveSession = () => {
    if (!messages.length) return;
    const transcript = messages
      .map((message) => `${message.role === "user" ? "Me" : message.name || "Companion"}: ${message.text}`)
      .join("\n\n");
    addJournalEntry({
      text: transcript,
      prompt: `Saved from ${mode.value}`,
    });
    toast.success("Saved to your Journal on this device.");
  };

  return (
    <PageContainer className="pt-7" testid="talk-page">
      <BrandHeader compact />

      <section className="mb-5">
        <p
          className="text-[10px] uppercase tracking-[0.34em] mb-2"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          Private conversation
        </p>
        <h1
          className="text-[42px] leading-[1.02] tracking-[-0.03em]"
          style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
        >
          What do you need
          <br />
          <em
            className="font-normal"
            style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}
          >
            right now?
          </em>
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>
          Choose a lens, not a label. Start anywhere. You can change direction as the conversation changes.
        </p>
      </section>

      <section
        className="rounded-2xl p-3.5 mb-5 flex items-start gap-2.5"
        style={{ background: "rgba(74,32,64,0.045)", border: "1px solid rgba(91,57,84,0.05)" }}
      >
        <LockKeyhole size={15} className="mt-0.5 shrink-0" style={{ color: "var(--un-plum)" }} />
        <p className="text-[11px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
          This conversation is not added to your Journal unless you choose <strong>Save</strong>. When you ask for an AI response, your message and up to eight previous messages are sent for processing; the Privacy page explains what leaves your device.
        </p>
      </section>

      <label className="flex items-start gap-2 text-xs mb-5">
        <input type="checkbox" checked={includeProtect} onChange={(e) => setIncludeProtect(e.target.checked)} />
        Include my Protect List in AI requests from this screen (optional).
      </label>

      <div className="grid grid-cols-3 gap-2 mb-5" aria-label="Conversation lenses">
        {COMPANION_MODES.map((item) => {
          const Icon = ICONS[item.icon] || Sparkles;
          const active = item.id === modeId;
          return (
            <button
              key={item.id}
              onClick={() => switchMode(item.id)}
              aria-pressed={active}
              className="rounded-3xl px-2 py-4 text-center un-press"
              style={{
                background: active ? "var(--un-plum)" : "rgba(91,57,84,0.045)",
                color: active ? "#fffaf5" : "var(--un-plum)",
                boxShadow: active ? "0 10px 26px rgba(74,45,72,0.12)" : "none",
              }}
            >
              <Icon size={18} className="mx-auto" strokeWidth={1.8} />
              <span className="block mt-2 text-[13px] font-medium">{item.value}</span>
            </button>
          );
        })}
      </div>

      <motion.section
        key={mode.id}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="un-card p-5 mb-4"
      >
        <p className="text-[10px] uppercase tracking-[0.26em]" style={{ color: "var(--un-text-3)" }}>
          {mode.eyebrow}
        </p>
        <p className="mt-2 text-[21px]" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>
          {mode.value}
        </p>
        <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>
          {mode.description}
        </p>
        <p className="mt-3 text-[11px] italic" style={{ color: "var(--un-text-3)", fontFamily: "Cormorant Garamond, serif" }}>
          {mode.value}
        </p>
      </motion.section>

      <section
        className="rounded-[1.75rem] overflow-hidden mb-4"
        style={{
          background: "rgba(255,252,248,0.82)",
          border: "1px solid rgba(91,57,84,0.06)",
          boxShadow: "0 8px 30px rgba(74,45,72,0.04)",
        }}
      >
        <div
          className="px-4 py-3 flex items-center justify-between gap-3"
          style={{ borderBottom: "1px solid rgba(91,57,84,0.07)" }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--un-text-3)" }}>
              Guide · {mode.value}
            </p>
            <p className="text-[12px] mt-0.5" style={{ color: "var(--un-text-2)" }}>
              This direction keeps its own private conversation here.
            </p>
          </div>
          <span
            className="shrink-0 rounded-full px-2.5 py-1 text-[10px]"
            style={{ background: "rgba(91,57,84,0.05)", color: "var(--un-plum)" }}
          >
            {messages.length ? `${messages.length} message${messages.length === 1 ? "" : "s"}` : "New thread"}
          </span>
        </div>

        <div className="min-h-[240px] max-h-[430px] overflow-y-auto px-4 py-4 space-y-3">
          {messages.length === 0 ? (
            <div className="py-10 text-center px-4">
              <p
                className="italic text-[19px] leading-relaxed"
                style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}
              >
                “{mode.starter}”
              </p>
              <p className="text-[11px] mt-3" style={{ color: "var(--un-text-3)" }}>
                You do not have to make it sound better than it feels.
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className="max-w-[88%] rounded-3xl px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap"
                  style={{
                    background: message.role === "user" ? "var(--un-plum)" : "var(--un-bg-2)",
                    color: message.role === "user" ? "#fffaf5" : "var(--un-text-1)",
                    borderBottomRightRadius: message.role === "user" ? "0.5rem" : undefined,
                    borderBottomLeftRadius: message.role === "assistant" ? "0.5rem" : undefined,
                  }}
                >
                  {message.text}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start">
              <div
                className="rounded-3xl px-4 py-3 text-[12px]"
                style={{ background: "var(--un-bg-2)", color: "var(--un-text-3)" }}
              >
                Thinking with you…
              </div>
            </div>
          )}
        </div>

        <div className="p-3" style={{ borderTop: "1px solid rgba(91,57,84,0.07)" }}>
          <div className="flex gap-2 items-end">
            <textarea
              aria-label="Your message"
              maxLength={4000}
              value={text}
              onChange={(e) =>
                setDrafts((current) => ({ ...current, [modeId]: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder="Say what feels true…"
              className="flex-1 resize-none rounded-2xl px-4 py-3 outline-none text-[14px]"
              style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            />
            <button
              onClick={send}
              disabled={!text.trim() || sending}
              aria-label="Send"
              className="w-11 h-11 rounded-2xl flex items-center justify-center un-press disabled:opacity-40"
              style={{ background: "var(--un-plum)", color: "#fffaf5" }}
            >
              <Send size={17} />
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <SoftButton
          variant="secondary"
          size="md"
          onClick={saveSession}
          disabled={!messages.length}
          iconLeft={<NotebookText size={14} />}
        >
          Save to Journal
        </SoftButton>
        <SoftButton
          variant="ghost"
          size="md"
          onClick={clearSession}
          disabled={!messages.length && !text}
          iconLeft={<Trash2 size={14} />}
        >
          Start fresh
        </SoftButton>
      </div>

      <section className="rounded-3xl px-5 py-4 mb-8" style={{ background: "rgba(91,57,84,0.035)" }}>
        <p className="text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
          <strong style={{ color: "var(--un-text-2)" }}>Beyond stigma.</strong> No identity label is required here. The goal is not perfection — it is a clearer, safer next choice.
        </p>
      </section>
    </PageContainer>
  );
}
